/**
 * Godot-inspired scene document model.
 * Scenes are node trees; scripts attach to nodes and run via SceneTree callbacks.
 */

export type SceneKind = 'level' | 'prefab';

export type NodeType =
  | 'Node'
  | 'Node3D'
  | 'MeshInstance3D'
  | 'Camera3D'
  | 'DirectionalLight3D'
  | 'OmniLight3D'
  | 'SpotLight3D'
  | 'CharacterBody3D'
  | 'StaticBody3D'
  | 'Marker3D'
  | 'PackedScene'
  | 'AnimationPlayer'
  | 'AudioStreamPlayer3D'
  | 'Timer'
  | 'CollisionShape3D'
  | 'Area3D';

export type ProcessMode = 'inherit' | 'pausable' | 'when_paused' | 'always' | 'disabled';

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export interface SceneTransform {
  position: Vec3;
  rotation: Vec3; // euler degrees (Godot-friendly)
  scale: Vec3;
}

export interface ScriptRef {
  /** Stable id for persistence */
  id: string;
  /** Display path e.g. res://scripts/player_controller.js */
  path: string;
  language: 'js';
  source: string;
}

export interface MaterialSlotRef {
  /** Material resource id or inline name */
  materialId?: string;
  /** Builtin preset key from materials.ts helpers */
  preset?: string;
  /** Surface index on multi-material meshes */
  surface?: number;
}

/** Per-type properties stored on the node (like Godot inspector fields). */
export interface NodeProps {
  // MeshInstance3D
  mesh?: 'box' | 'plane' | 'sphere' | 'cylinder' | 'capsule' | 'piste' | 'external';
  modelUrl?: string;
  material?: MaterialSlotRef;
  castShadow?: boolean;
  receiveShadow?: boolean;
  // Lights
  color?: string; // #rrggbb
  energy?: number;
  range?: number;
  angle?: number; // spot degrees
  // Camera
  fov?: number;
  near?: number;
  far?: number;
  current?: boolean;
  // Character / gameplay
  characterId?: string;
  colorId?: string;
  isPlayer?: boolean;
  // PackedScene
  scenePath?: string;
  // AnimationPlayer
  autoplay?: string;
  /** Simple built-in clip names: spin_y | bob | pulse | none — or custom from animations */
  defaultClip?: string;
  animSpeed?: number;
  /** Custom clip library (AnimLibrary JSON) */
  animations?: import('./AnimData').AnimLibrary;
  /** Runtime playhead (not always persisted) */
  animTime?: number;
  animPlaying?: boolean;
  // AudioStreamPlayer3D
  stream?: string; // named sfx: lunge | parry | touch | allez | en_garde | ui | tone
  autoplayAudio?: boolean;
  volumeDb?: number;
  pitchScale?: number;
  maxDistance?: number;
  // Timer
  waitTime?: number;
  oneShot?: boolean;
  autostart?: boolean;
  // CollisionShape3D
  shape?: 'box' | 'sphere' | 'capsule' | 'cylinder';
  shapeSize?: { x: number; y: number; z: number };
  disabled?: boolean;
  // Area3D
  monitoring?: boolean;
  monitorable?: boolean;
  collisionLayer?: number;
  collisionMask?: number;
  // Generic
  groups?: string[];
  [key: string]: unknown;
}

export interface SceneNodeData {
  id: string;
  name: string;
  type: NodeType;
  visible: boolean;
  processMode: ProcessMode;
  transform: SceneTransform;
  props: NodeProps;
  /** Attached script id (in scene.scripts) */
  scriptId: string | null;
  /** Script exported property overrides */
  scriptProps: Record<string, unknown>;
  children: SceneNodeData[];
  collapsed?: boolean;
}

export interface SceneResource {
  format: 1;
  id: string;
  name: string;
  kind: SceneKind;
  path: string; // e.g. res://levels/main_arena.tscn
  root: SceneNodeData;
  scripts: Record<string, ScriptRef>;
  /** Optional notes */
  description?: string;
  updatedAt: string;
  builtin?: boolean;
}

export const NODE_TYPE_META: Record<
  NodeType,
  { label: string; icon: string; hasTransform: boolean; category: string }
> = {
  Node: { label: 'Node', icon: '◇', hasTransform: false, category: 'Base' },
  Node3D: { label: 'Node3D', icon: '▣', hasTransform: true, category: 'Node3D' },
  MeshInstance3D: { label: 'MeshInstance3D', icon: '▦', hasTransform: true, category: 'Node3D' },
  Camera3D: { label: 'Camera3D', icon: '◎', hasTransform: true, category: 'Node3D' },
  DirectionalLight3D: { label: 'DirectionalLight3D', icon: '☀', hasTransform: true, category: 'Light' },
  OmniLight3D: { label: 'OmniLight3D', icon: '💡', hasTransform: true, category: 'Light' },
  SpotLight3D: { label: 'SpotLight3D', icon: '🔦', hasTransform: true, category: 'Light' },
  CharacterBody3D: { label: 'CharacterBody3D', icon: '♟', hasTransform: true, category: 'Physics' },
  StaticBody3D: { label: 'StaticBody3D', icon: '▬', hasTransform: true, category: 'Physics' },
  Marker3D: { label: 'Marker3D', icon: '✚', hasTransform: true, category: 'Node3D' },
  PackedScene: { label: 'Instance (PackedScene)', icon: '⧉', hasTransform: true, category: 'Scene' },
  AnimationPlayer: { label: 'AnimationPlayer', icon: '▶', hasTransform: false, category: 'Animation' },
  AudioStreamPlayer3D: { label: 'AudioStreamPlayer3D', icon: '♪', hasTransform: true, category: 'Audio' },
  Timer: { label: 'Timer', icon: '⏱', hasTransform: false, category: 'Base' },
  CollisionShape3D: { label: 'CollisionShape3D', icon: '⬡', hasTransform: true, category: 'Physics' },
  Area3D: { label: 'Area3D', icon: '◯', hasTransform: true, category: 'Physics' },
};

export function uid(prefix = 'n'): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;
}

export function defaultTransform(): SceneTransform {
  return {
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    scale: { x: 1, y: 1, z: 1 },
  };
}

export function defaultPropsFor(type: NodeType): NodeProps {
  switch (type) {
    case 'MeshInstance3D':
      return { mesh: 'box', castShadow: true, receiveShadow: true, material: { preset: 'jacket' } };
    case 'DirectionalLight3D':
      return { color: '#fff5e8', energy: 1.6 };
    case 'OmniLight3D':
      return { color: '#aaccff', energy: 4, range: 12 };
    case 'SpotLight3D':
      return { color: '#6ee7ff', energy: 20, range: 18, angle: 35 };
    case 'Camera3D':
      return { fov: 45, near: 0.05, far: 200, current: false };
    case 'CharacterBody3D':
      return { characterId: 'fencer', colorId: 'blue', isPlayer: true };
    case 'PackedScene':
      return { scenePath: 'res://prefabs/player.tscn' };
    case 'AnimationPlayer':
      return {
        defaultClip: 'spin_y',
        animSpeed: 1,
        autoplay: 'spin_y',
        animPlaying: true,
        animations: undefined, // filled at runtime via defaultAnimLibrary when missing
      };
    case 'AudioStreamPlayer3D':
      return { stream: 'ui', autoplayAudio: false, volumeDb: 0, pitchScale: 1, maxDistance: 20 };
    case 'Timer':
      return { waitTime: 1, oneShot: false, autostart: true };
    case 'CollisionShape3D':
      return { shape: 'capsule', shapeSize: { x: 0.35, y: 1.0, z: 0.35 }, disabled: false };
    case 'Area3D':
      return { monitoring: true, monitorable: true, collisionLayer: 1, collisionMask: 1 };
    default:
      return {};
  }
}

export function createNode(type: NodeType, name?: string): SceneNodeData {
  const base = name || type;
  return {
    id: uid('node'),
    name: base,
    type,
    visible: true,
    processMode: 'inherit',
    transform: defaultTransform(),
    props: defaultPropsFor(type),
    scriptId: null,
    scriptProps: {},
    children: [],
    collapsed: false,
  };
}
